import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import MobileMenuSheet from './MobileMenuSheet';
import { useAuth } from '../../contexts/AuthContext';
import { useActiveMenu } from '../../features/menu/useActiveMenu';
import { useFilteredMenu } from '../../features/menu/useFilteredMenu';
import { navigateToVueRoute } from '../../utils/navigationHandler';
import { logger } from '../../utils/logger';

/**
 * Fixed-bottom navigation bar for mobile (< 768px). Renders EVERY
 * top-level rail from the permission-filtered `MENU_CONFIG` — the same
 * list the desktop `AsideMenu` shows — plus a trailing Logout tab, so
 * mobile users can reach every module the sidebar offers.
 *
 * The bar scrolls HORIZONTALLY when the rails exceed the screen width:
 * each tab keeps a fixed minimum width (`flex-none`) and the strip pans
 * with a hidden scrollbar, so nothing is squashed or cut off.
 *
 * Tap behaviour mirrors the aside menu:
 *   • rail with children  → toggles the slide-up `<MobileMenuSheet>`
 *     showing that rail's sub-pages (tap again to close, tap another
 *     rail to swap contents);
 *   • leaf rail           → navigates — react-router for React routes,
 *     cross-origin redirect for Vue routes;
 *   • Logout              → same auth logout the aside menu performs.
 */

const BAR_CLASS =
  // z-index sits ABOVE both:
  //   • the slide-up `<MobileMenuSheet>` (z-91)
  //   • the shared `<SlideOverlay>` backdrop (z-1100), used by the
  //     order details panel and AR Contacts
  // so the bar stays visible and tappable in every overlay scenario.
  'fixed left-0 right-0 bottom-0 z-[1102] flex md:hidden ' +
  // Horizontal pan when the rail set outgrows the viewport; the
  // scrollbar is hidden so the strip reads as a native tab bar.
  'overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ' +
  'bg-[var(--color-bg-elevated)] border-t border-solid border-[var(--color-border)] ' +
  // Honor the iOS Home Indicator safe area so the labels don't collide
  // with the system gesture bar at the bottom of the screen.
  'pb-[env(safe-area-inset-bottom)]';

const TAB_CLASS =
  // `flex-none` + a fixed minimum width per tab: in the scrollable strip
  // the tabs must keep their size (flex-1 would squash them once the
  // full rail set mounts) — overflow provides the room instead.
  'flex-none min-w-[76px] px-2 inline-flex flex-col items-center justify-center gap-1 ' +
  'py-2 cursor-pointer bg-transparent border-0 ' +
  'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] ' +
  'transition-colors duration-fast';

const TAB_ACTIVE_CLASS =
  'text-[var(--color-primary)] hover:text-[var(--color-primary)]';

const TAB_LABEL_CLASS = 'text-[12px] leading-tight whitespace-nowrap';
const TAB_LABEL_ACTIVE_CLASS = 'font-semibold';

/** Minimal logout glyph — the desktop rail renders Logout without an
 *  icon; the tab bar needs one to keep the strip visually uniform. */
function LogoutIcon({ size = 22 }: Readonly<{ size?: number }>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </svg>
  );
}

export function BottomTabBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const filteredMenu = useFilteredMenu();
  const { activeRailId } = useActiveMenu(filteredMenu);

  /** When non-null, the slide-up `<MobileMenuSheet>` is open and
   *  rendering the children of the matching `MENU_CONFIG` top-level
   *  node. Closing the sheet sets this back to `null`. */
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  return (
    <>
      <nav className={BAR_CLASS} aria-label="Primary">
        {filteredMenu.map((rail) => {
          const Icon = rail.icon;
          const hasChildren = Boolean(rail.children?.length);
          const active = rail.id === activeRailId;
          return (
            <button
              key={rail.id}
              type="button"
              className={[TAB_CLASS, active ? TAB_ACTIVE_CLASS : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                // 1) Rail with children → toggle the slide-up submenu
                //    sheet. Same tab twice = close. Different tab =
                //    switch to that menu (sheet stays open, contents
                //    swap) — the user sees the sub-pages instead of
                //    being thrown to a parent route.
                if (hasChildren) {
                  setOpenMenuId((prev) => (prev === rail.id ? null : rail.id));
                  return;
                }
                // 2) Leaf rail → direct navigation, mirroring the aside
                //    menu: react-router in-tab for React routes, a NEW TAB
                //    cross-origin for Vue routes so this app is never torn
                //    down by leaving for the ERP. Close any open sheet so
                //    the user lands on a clean page either way.
                if (rail.route) {
                  setOpenMenuId(null);
                  if (rail.isReactRoute) navigate(rail.route);
                  else navigateToVueRoute({ vuePath: rail.route, openInNewTab: true });
                  return;
                }
                // 3) Neither — module not yet wired. Log a placeholder.
                logger.info(`[BottomTabBar] ${rail.label} tap (no route wired)`);
              }}
              aria-current={active ? 'page' : undefined}
              aria-label={t(rail.labelKey, { defaultValue: rail.label })}
              aria-haspopup={hasChildren ? 'menu' : undefined}
              aria-expanded={hasChildren ? openMenuId === rail.id : undefined}
            >
              {Icon ? <Icon size={22} ariaHidden /> : null}
              <span
                className={[
                  TAB_LABEL_CLASS,
                  active ? TAB_LABEL_ACTIVE_CLASS : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {t(rail.labelKey, { defaultValue: rail.label })}
              </span>
            </button>
          );
        })}
        {/* Desktop-parity Logout — the aside menu's trailing rail. */}
        <button
          type="button"
          className={TAB_CLASS}
          onClick={() => {
            setOpenMenuId(null);
            void logout();
          }}
          aria-label={t('menu.logout', 'Logout')}
        >
          <LogoutIcon />
          <span className={TAB_LABEL_CLASS}>{t('menu.logout', 'Logout')}</span>
        </button>
      </nav>
      <MobileMenuSheet
        menuId={openMenuId}
        open={openMenuId !== null}
        onClose={() => setOpenMenuId(null)}
      />
    </>
  );
}

export default BottomTabBar;
